// P2P Signaling Client for WebRTC Connection Establishment
export class P2PSignalingClient {
  private static instance: P2PSignalingClient;
  private signalingSocket: WebSocket | null = null;
  private localPeerId: string = '';
  private roomId: string = '';
  private offerHandlers = new Map<string, (offer: RTCSessionDescriptionInit) => void>();
  private answerHandlers = new Map<string, (answer: RTCSessionDescriptionInit) => void>();
  private iceCandidateHandlers = new Map<string, (candidate: RTCIceCandidate) => void>();

  private constructor() {}

  static getInstance(): P2PSignalingClient {
    if (!P2PSignalingClient.instance) {
      P2PSignalingClient.instance = new P2PSignalingClient();
    }
    return P2PSignalingClient.instance;
  }

  // Connect to signaling server for WebRTC coordination
  async connectToSignaling(signalingUrl: string, roomId: string, peerId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.roomId = roomId;
      this.localPeerId = peerId;

      this.signalingSocket = new WebSocket(signalingUrl);

      this.signalingSocket.onopen = () => {
        console.log('Connected to signaling server');
        
        // Join room for P2P coordination
        this.sendSignalingMessage({
          type: 'join_p2p_room',
          roomId: this.roomId,
          peerId: this.localPeerId
        });

        resolve();
      };

      this.signalingSocket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          this.handleSignalingMessage(message);
        } catch (error) {
          console.error('Failed to parse signaling message:', error);
        }
      };

      this.signalingSocket.onerror = (error) => {
        console.error('Signaling socket error:', error);
        reject(error);
      };

      this.signalingSocket.onclose = () => {
        console.log('Signaling connection closed');
      };

      // Connection timeout
      setTimeout(() => {
        if (this.signalingSocket?.readyState !== WebSocket.OPEN) {
          reject(new Error('Signaling connection timeout'));
        }
      }, 5000);
    });
  }

  // Handle incoming signaling messages
  private handleSignalingMessage(message: any): void {
    switch (message.type) {
      case 'peer_joined':
        console.log(`Peer ${message.peerId} joined room`);
        this.onPeerJoined?.(message.peerId);
        break;

      case 'peer_left':
        console.log(`Peer ${message.peerId} left room`);
        this.onPeerLeft?.(message.peerId);
        break;

      case 'webrtc_offer':
        const offerHandler = this.offerHandlers.get(message.fromPeer);
        if (offerHandler) {
          offerHandler(message.offer);
        }
        break;

      case 'webrtc_answer':
        const answerHandler = this.answerHandlers.get(message.fromPeer);
        if (answerHandler) {
          answerHandler(message.answer);
        }
        break;

      case 'webrtc_ice_candidate':
        const iceHandler = this.iceCandidateHandlers.get(message.fromPeer);
        if (iceHandler) {
          iceHandler(new RTCIceCandidate(message.candidate));
        }
        break;

      case 'room_peers':
        console.log('Current room peers:', message.peers);
        this.onRoomPeersUpdate?.(message.peers);
        break;

      default:
        console.log('Received signaling message:', message);
    }
  }

  // Send signaling message to server
  private sendSignalingMessage(message: any): void {
    if (this.signalingSocket?.readyState === WebSocket.OPEN) {
      this.signalingSocket.send(JSON.stringify(message));
    }
  }

  // Send WebRTC offer to a peer
  sendOffer(toPeer: string, offer: RTCSessionDescriptionInit): void {
    this.sendSignalingMessage({
      type: 'webrtc_offer',
      fromPeer: this.localPeerId,
      toPeer,
      offer
    });
  }

  // Send WebRTC answer to a peer
  sendAnswer(toPeer: string, answer: RTCSessionDescriptionInit): void {
    this.sendSignalingMessage({
      type: 'webrtc_answer',
      fromPeer: this.localPeerId,
      toPeer,
      answer
    });
  }

  // Send ICE candidate to a peer
  sendIceCandidate(toPeer: string, candidate: RTCIceCandidate): void {
    this.sendSignalingMessage({
      type: 'webrtc_ice_candidate',
      fromPeer: this.localPeerId,
      toPeer,
      candidate: {
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMLineIndex
      }
    });
  }

  // Register handler for incoming WebRTC offers
  onOffer(fromPeer: string, handler: (offer: RTCSessionDescriptionInit) => void): void {
    this.offerHandlers.set(fromPeer, handler);
  }

  // Register handler for incoming WebRTC answers
  onAnswer(fromPeer: string, handler: (answer: RTCSessionDescriptionInit) => void): void {
    this.answerHandlers.set(fromPeer, handler);
  }

  // Register handler for incoming ICE candidates
  onIceCandidate(fromPeer: string, handler: (candidate: RTCIceCandidate) => void): void {
    this.iceCandidateHandlers.set(fromPeer, handler);
  }

  // Request current peers in room
  requestRoomPeers(): void {
    this.sendSignalingMessage({
      type: 'get_room_peers',
      roomId: this.roomId
    });
  }

  // Announce reconnection attempt
  announceReconnection(): void {
    this.sendSignalingMessage({
      type: 'peer_reconnecting',
      roomId: this.roomId,
      peerId: this.localPeerId,
      timestamp: Date.now()
    });
  }

  // Leave room and disconnect
  disconnect(): void {
    if (this.signalingSocket) {
      this.sendSignalingMessage({
        type: 'leave_p2p_room',
        roomId: this.roomId,
        peerId: this.localPeerId
      });

      this.signalingSocket.close();
      this.signalingSocket = null;
    }

    this.offerHandlers.clear();
    this.answerHandlers.clear();
    this.iceCandidateHandlers.clear();
  }

  // Check if signaling is connected
  isConnected(): boolean {
    return this.signalingSocket?.readyState === WebSocket.OPEN;
  }

  // Event handlers (set by P2PReconnectionManager)
  onPeerJoined?: (peerId: string) => void;
  onPeerLeft?: (peerId: string) => void;
  onRoomPeersUpdate?: (peers: string[]) => void;
}

// P2P Reconnection Manager - orchestrates the entire reconnection process
export class P2PReconnectionManager {
  private static instance: P2PReconnectionManager;
  private stateManager: any; // P2PStateManager
  private signalingClient: P2PSignalingClient;
  private peerConnections = new Map<string, RTCPeerConnection>();
  private reconnectionAttempts = new Map<string, number>();
  private readonly MAX_RECONNECTION_ATTEMPTS = 3;

  private constructor() {
    this.signalingClient = P2PSignalingClient.getInstance();
    this.setupSignalingHandlers();
  }

  static getInstance(): P2PReconnectionManager {
    if (!P2PReconnectionManager.instance) {
      P2PReconnectionManager.instance = new P2PReconnectionManager();
    }
    return P2PReconnectionManager.instance;
  }

  // Set the state manager instance
  setStateManager(stateManager: any): void {
    this.stateManager = stateManager;
  }

  // Setup signaling event handlers
  private setupSignalingHandlers(): void {
    this.signalingClient.onPeerJoined = (peerId: string) => {
      this.handlePeerJoined(peerId);
    };

    this.signalingClient.onPeerLeft = (peerId: string) => {
      this.handlePeerLeft(peerId);
    };

    this.signalingClient.onRoomPeersUpdate = (peers: string[]) => {
      this.handleRoomPeersUpdate(peers);
    };
  }

  // Handle peer joined event
  private async handlePeerJoined(peerId: string): Promise<void> {
    console.log(`Initiating P2P connection to new peer: ${peerId}`);
    await this.establishPeerConnection(peerId, true);
  }

  // Handle peer left event
  private handlePeerLeft(peerId: string): void {
    console.log(`Peer ${peerId} left, cleaning up connection`);
    const peerConnection = this.peerConnections.get(peerId);
    if (peerConnection) {
      peerConnection.close();
      this.peerConnections.delete(peerId);
    }
    this.reconnectionAttempts.delete(peerId);
  }

  // Handle room peers update
  private async handleRoomPeersUpdate(peers: string[]): Promise<void> {
    console.log('Room peers updated:', peers);
    
    // Try to establish connections to all peers
    for (const peerId of peers) {
      if (!this.peerConnections.has(peerId)) {
        await this.establishPeerConnection(peerId, false);
      }
    }
  }

  // Establish P2P connection with a peer
  private async establishPeerConnection(peerId: string, isInitiator: boolean): Promise<void> {
    try {
      // Create peer connection via state manager
      const peerConnection = await this.stateManager.connectToPeer(peerId, isInitiator);
      this.peerConnections.set(peerId, peerConnection);

      // Setup WebRTC signaling handlers
      this.signalingClient.onOffer(peerId, async (offer) => {
        await peerConnection.setRemoteDescription(offer);
        const answer = await peerConnection.createAnswer();
        await peerConnection.setLocalDescription(answer);
        this.signalingClient.sendAnswer(peerId, answer);
      });

      this.signalingClient.onAnswer(peerId, async (answer) => {
        await peerConnection.setRemoteDescription(answer);
      });

      this.signalingClient.onIceCandidate(peerId, async (candidate) => {
        await peerConnection.addIceCandidate(candidate);
      });

      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          this.signalingClient.sendIceCandidate(peerId, event.candidate);
        }
      };

      // If initiator, create offer
      if (isInitiator) {
        const offer = await peerConnection.createOffer();
        await peerConnection.setLocalDescription(offer);
        this.signalingClient.sendOffer(peerId, offer);
      }

    } catch (error) {
      console.error(`Failed to establish P2P connection to ${peerId}:`, error);
      
      // Track failed attempts
      const attempts = this.reconnectionAttempts.get(peerId) || 0;
      if (attempts < this.MAX_RECONNECTION_ATTEMPTS) {
        this.reconnectionAttempts.set(peerId, attempts + 1);
        
        // Retry after delay
        setTimeout(() => {
          this.establishPeerConnection(peerId, isInitiator);
        }, 2000 * (attempts + 1));
      }
    }
  }

  // Main P2P reconnection method
  async attemptP2PReconnection(roomId: string, playerId: string, signalingUrl: string): Promise<boolean> {
    try {
      console.log('Starting P2P reconnection process...');

      // 1. Connect to signaling server for WebRTC coordination
      await this.signalingClient.connectToSignaling(signalingUrl, roomId, playerId);
      
      // 2. Announce reconnection to get current peer list
      this.signalingClient.announceReconnection();
      this.signalingClient.requestRoomPeers();

      // 3. Wait for peer discovery and connection establishment
      await new Promise(resolve => setTimeout(resolve, 3000));

      // 4. Attempt state recovery from peers
      const connectedPeers = Array.from(this.peerConnections.values())
        .filter(pc => pc.connectionState === 'connected');

      if (connectedPeers.length > 0) {
        console.log(`Successfully connected to ${connectedPeers.length} peers for P2P recovery`);
        return true;
      } else {
        throw new Error('No peers available for P2P reconnection');
      }

    } catch (error) {
      console.error('P2P reconnection failed:', error);
      return false;
    }
  }

  // Get P2P connection status
  getConnectionStatus(): any {
    const totalConnections = this.peerConnections.size;
    const connectedPeers = Array.from(this.peerConnections.entries())
      .filter(([_, pc]) => pc.connectionState === 'connected');

    return {
      totalPeers: totalConnections,
      connectedPeers: connectedPeers.length,
      signalingConnected: this.signalingClient.isConnected(),
      peerConnectionStates: Array.from(this.peerConnections.entries()).map(([peerId, pc]) => ({
        peerId,
        connectionState: pc.connectionState,
        iceConnectionState: pc.iceConnectionState
      }))
    };
  }

  // Disconnect all P2P connections
  disconnect(): void {
    this.peerConnections.forEach((pc, peerId) => {
      console.log(`Closing P2P connection to ${peerId}`);
      pc.close();
    });

    this.peerConnections.clear();
    this.reconnectionAttempts.clear();
    this.signalingClient.disconnect();
  }
}